import { useCallback, useMemo, useRef } from 'react';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useSort } from './useSort';
import { useTableTheme } from './useTableTheme';

export const useTable = ({
  items,
  columnDefinition,
  persistColumnKey,
  isLoadingMore,
  isInitialized,
  initialSortBy,
  onSort,
  canSelect,
  onSelect,
  selectedAll,
  onColumnAction,
  cellStyle = {},
  useHorizontalActions = true,
  canSort = true,
  sortColumns = [],
}) => {
  const tableRef = useRef(null);

  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    persistColumnKey || 'ag-grid-table-v1',
    columnDefinition.map((item) => ({
      ...item,
      show: true,
    })),
  );

  const sortedColumns = useMemo(() => [...sortColumns, ...selectedColumns], [sortColumns, selectedColumns]);
  const theme = useTableTheme();
  const { selectedColumn, setSelectedColumn, handleSort } = useSort({
    columnDefinition: sortedColumns,
    initialSortBy,
    onSort,
    canSort,
  });

  const hideColumn = useCallback(
    (column) => {
      onColumnAction('hide', { payload: column });
      setSelectedColumns(
        (prev) =>
          prev.map((item) => {
            if (item.accessor === column.accessor) {
              return { ...item, show: false };
            }
            return item;
          }),
        // eslint-disable-next-line function-paren-newline
      );
      tableRef.current.api.refreshHeader();
    },
    [onColumnAction, setSelectedColumns],
  );

  const flattenedColumns = useMemo(
    () =>
      columnDefinition.reduce((acc, col) => {
        acc[col.accessor] = col;
        return acc;
      }, {}),
    [columnDefinition],
  );

  const columnDefs = useMemo(() => {
    const columns = canSelect
      ? [
          {
            pinned: 'left',
            field: 'selected',
            accessor: 'selected',
            headerName: 'selected',
            minWidth: 32,
            resizable: false,
            width: 32,
            cellRenderer: 'customCheckboxRenderer',
            sortable: false,
            filter: false,
            lockPosition: 'left',
            suppressMovable: true,
            headerComponent: 'customCheckboxRenderer',
            headerClass: 'ag-header-cell-checkbox',
            headerComponentParams: {
              onSelect,
              selectedAll,
              isBulkSelection: true,
              sx: {
                pt: '8px',
              },
            },
            cellRendererParams: {
              onSelect,
              selectedAll: false,
              isBulkSelection: false,
            },
            contextMenuItems: ['copy', 'paste', 'delete'],
            cellClass: 'ag-column-checkbox',
          },
        ]
      : [];

    const formatColumn = (column) => {
      const {
        accessor,
        Header,
        disableSortBy,
        disablePadding,
        tooltip,
        cellRenderer,
        width,
        pinned,
        rowGroup,
        show,
        children,
        orderIndex,
        columnSelection,
        ...props
      } = flattenedColumns[column.accessor];

      const headerParams = {
        handleSort,
        field: accessor,
        sortedBy: initialSortBy,
        tooltip,
        hideColumn,
        disableSortBy,
        displayName: Header,
        rowGroup,
        columnSelection,
        itemCount: items.length,
      };

      const selected = selectedColumns.find((item) => item.accessor === accessor);
      return {
        field: accessor,
        accessor,
        Header,
        headerName: Header,
        hide: selected?.show !== undefined ? !selected.show : true,
        sortable: false,
        cellRenderer: cellRenderer ?? 'tableRow',
        filter: false,
        minWidth: typeof width === 'string' ? parseInt(width.replace('px', ''), 10) : 300,
        pinned,
        rowGroup: rowGroup ?? false,
        headerComponent: 'tableHeader',
        headerComponentParams: headerParams,
        cellRendererParams: {
          isFirstColumn: selectedColumns[0].accessor === accessor,
          useHorizontalActions,
          cellStyle,
        },
        ...props,
      };
    };

    return columns.concat(selectedColumns.map(formatColumn));
  }, [
    canSelect,
    cellStyle,
    flattenedColumns,
    handleSort,
    hideColumn,
    initialSortBy,
    items.length,
    onSelect,
    selectedAll,
    selectedColumns,
    useHorizontalActions,
  ]);

  const onColumnMoved = useCallback(
    ({ column, toIndex, source, finished }) => {
      if (source === 'uiColumnMoved' && finished) {
        const index = selectedColumns.findIndex(({ accessor }) => accessor === column.colId);
        const newColumns = [...selectedColumns];
        const indexToMove = toIndex - 1;

        const isMovable = ![
          'selected',
          ...columnDefinition.filter((item) => item.suppressMovable).map((item) => item.accessor),
        ].includes(selectedColumns[indexToMove].accessor);

        if (indexToMove >= 0 && indexToMove < newColumns.length) {
          if (isMovable) {
            newColumns.splice(indexToMove, 0, newColumns.splice(index, 1)[0]);
          }

          setSelectedColumns(newColumns);
        }
      }
    },
    [columnDefinition, selectedColumns, setSelectedColumns],
  );

  const showEmptyView = items.length === 0 && !isLoadingMore && isInitialized;

  return {
    tableRef,
    selectedColumns,
    setSelectedColumns,
    selectedColumn,
    setSelectedColumn,
    showEmptyView,
    handleSort,
    columnDefs,
    theme,
    flattenedColumns,
    onColumnMoved,
    sortedColumns,
  };
};
